import { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useStudentAcademic } from '../../../api/hooks';
import type { AcademicResultRow } from '../../../api/types';
import { Badge, EmptyState, PagedFooter, SortableTh, Table, Td, Term } from '../../../components/ui';
import { FaFileAlt } from '../../../components/ui/icons';
import { useSortableTable, SortColumn } from '../../../lib/useSortableTable';
import { usePagination } from '../../../lib/usePagination';
import { TH_RULE } from '../../../lib/tableRules';
import Box from '../components/Box';
import { dayMonth } from '../lib/format';
import ExamBreakdown from './ExamBreakdown';
import LateBadge from '../../../components/LateBadge';
import { FAILED, LOADING, SubjectPills, type SubjectKey, useStudentSubjects } from './shared';

const COLUMNS: SortColumn<AcademicResultRow>[] = [
  { key: 'date', accessor: (r) => r.created, type: 'date' },
  { key: 'subject', accessor: (r) => r.subject },
  { key: 'exam', accessor: (r) => r.exam_name },
  { key: 'score', accessor: (r) => r.score_percentage, type: 'number' },
  { key: 'rwu', accessor: (r) => r.right, type: 'number' },
];

/** Every scored exam attempt; a row opens that exam broken down against the class. */
export default function ExamsView({ id, studentName, openResult, onOpenResult }: {
  id: number; studentName: string; openResult: number | null; onOpenResult: (resultId: number | null) => void;
}) {
  const [subject, setSubject] = useState<SubjectKey>('all');
  const subjects = useStudentSubjects(id);
  const examsQ = useStudentAcademic(id);
  const filtered = useMemo(() => {
    const all = examsQ.data?.results ?? [];
    if (subject === 'all') return all;
    const name = subjects.find((s) => s.id === subject)?.name;
    return all.filter((r) => r.subject === name);
  }, [examsQ.data, subject, subjects]);
  const { rows, sort, toggle } = useSortableTable(filtered, COLUMNS, { key: 'date', dir: 'desc' });
  const pg = usePagination(rows, 10, `${subject}|${sort?.key}|${sort?.dir}`);

  if (openResult != null) {
    return <ExamBreakdown studentId={id} resultId={openResult} studentName={studentName} onBack={() => onOpenResult(null)} />;
  }
  return (
    <div className="space-y-4">
      <SubjectPills id={id} value={subject} onChange={setSubject} />
      <Box title="Exam attempts" help="Every scored exam attempt by this student. Open one to see it question by question against the class."
        actions={examsQ.data && examsQ.data.results.length > 0
          ? <span className="text-xs text-muted">{rows.length} {rows.length === 1 ? 'attempt' : 'attempts'}</span> : undefined}>
        {examsQ.isLoading ? LOADING :
         examsQ.isError || !examsQ.data ? FAILED :
         rows.length === 0 ? (
           <EmptyState compact icon={<FaFileAlt />} title="No exam attempts yet" body="When this student takes exams, results will appear here." />
         ) : (
           <>
             <Table minWidth={640}>
               <thead>
                 <tr>
                   <SortableTh columnKey="date" sort={sort} onToggle={toggle} className={TH_RULE}>Date</SortableTh>
                   <SortableTh columnKey="subject" sort={sort} onToggle={toggle} className={TH_RULE}>Subject</SortableTh>
                   <SortableTh columnKey="exam" sort={sort} onToggle={toggle} className={TH_RULE}>Exam</SortableTh>
                   <SortableTh columnKey="score" sort={sort} onToggle={toggle} align="right" className={TH_RULE}>Score</SortableTh>
                   <SortableTh columnKey="rwu" sort={sort} onToggle={toggle} align="right" className={`whitespace-nowrap ${TH_RULE}`} help="right_wrong_blank">Right / wrong / blank</SortableTh>
                   <th className={TH_RULE} aria-label="Open" />
                 </tr>
               </thead>
               <tbody>
                 {pg.pageItems.map((r) => (
                   <tr key={r.id} className="cursor-pointer hover:bg-surface-2" onClick={() => onOpenResult(r.id)}>
                     <Td className="whitespace-nowrap font-mono text-[13px] font-medium">{dayMonth(r.created)}</Td>
                     <Td>{r.subject ?? '—'}{r.chapter && <span className="block text-xs text-muted">{r.chapter}</span>}</Td>
                     <Td>
                       <button type="button" className="text-left font-semibold text-ink hover:underline"
                         onClick={(e) => { e.stopPropagation(); onOpenResult(r.id); }} aria-label={`Open ${r.exam_name ?? 'exam'} breakdown`}>
                         {r.exam_name ?? '—'}
                       </button>
                       {r.source === 'paper' && <Badge tone="neutral" size="sm" className="ml-1.5"><Term k="delivery">Paper</Term></Badge>}
                       {r.late && <LateBadge className="ml-1.5" unverified={!!r.late_unverified} />}
                     </Td>
                     <Td align="right" className={`whitespace-nowrap font-mono font-semibold tabular-nums ${r.score_percentage !== null && r.score_percentage < 50 ? 'text-danger' : ''}`}>
                       {r.score_percentage !== null ? `${r.score_percentage.toFixed(1)}%` : '—'}
                     </Td>
                     <Td align="right" className="whitespace-nowrap font-mono font-medium text-muted tabular-nums">{r.right} / {r.wrong} / {r.unanswered}</Td>
                     <Td align="right" className="text-muted"><ChevronRight size={16} aria-hidden /></Td>
                   </tr>
                 ))}
               </tbody>
             </Table>
             <PagedFooter className="mt-3.5" label="Exam attempts pagination" {...pg} onPage={pg.setPage} />
           </>
         )}
      </Box>
    </div>
  );
}
